// Cockpit direction (`GET /dashboard/business-cockpit`) recalculé sur l'appareil.
// Port de backend/app/core/business_cockpit.py (transport + carte « stock et
// alertes »), vérifié par les jeux communs offline_parity/cockpit.json.
//
// Les sections « stations » et « contrôle carburant » dépendent de données qui
// ne vivent pas ici (ventes, cuves, demandes…): elles gardent leur dernière
// version connue. Les scores d'intégrité et dossiers de preuve viennent de la
// dernière réponse du serveur (`inputs`, demandés avec `include_inputs`).
import { calculerSoldes } from './grandLivre';

const STATUTS_FR = {
  CREATED: 'Créé', VALIDATED: 'Validé', EN_ROUTE_TO_BORDER: 'En route vers la frontière',
  AT_CUSTOMS_EXIT: 'À la douane de sortie', CLEARED_CUSTOMS_EXIT: 'Douane de sortie franchie',
  EN_ROUTE_TO_LOADING: 'En route vers le chargement', LOADING: 'En chargement', LOADED: 'Chargé',
  EN_ROUTE_TO_ENTRY_CUSTOMS: "En route vers la douane d'entrée", AT_CUSTOMS_ENTRY: "À la douane d'entrée",
  CLEARED_CUSTOMS_ENTRY: "Douane d'entrée franchie", ARRIVED_AT_DEPOT: 'Arrivé au dépôt',
  EN_ROUTE_TO_DELIVERY: 'En route vers la livraison', IN_TRANSIT: 'En transit',
  DELIVERED: 'Livré', PARTIALLY_DELIVERED: 'Livré partiellement', COMPLETED: 'Terminé',
  POD_REQUIRED: 'Bon de livraison à fournir', POD_REVIEW: 'Bon de livraison à vérifier',
  BILLING_READY: 'Prêt à facturer', FAILED_RECONCILIATION: 'Écart à régulariser',
  BLOCKED_AUDIT: 'Bloqué pour contrôle', CANCELLED: 'Annulé',
};

const statutFr = (statut) => {
  const code = String(statut ?? '').trim().toUpperCase();
  return code ? STATUTS_FR[code] || 'Statut à vérifier' : '';
};

const num = (valeur) => {
  if (valeur === null || valeur === undefined || valeur === '') return 0;
  const n = Number(valeur);
  return Number.isFinite(n) ? n : 0;
};
// `x or 0` de Python: faux (0, null, "") devient 0.
const ou = (...valeurs) => {
  for (const v of valeurs) if (v) return v;
  return valeurs[valeurs.length - 1] || 0;
};

// Python arrondit à l'entier pair le plus proche (round(2.5) == 2): même résultat ici.
const arrondiPair = (x) => {
  const bas = Math.floor(x);
  const reste = x - bas;
  if (reste < 0.5) return bas;
  if (reste > 0.5) return bas + 1;
  return bas % 2 === 0 ? bas : bas + 1;
};

const MARQUEURS = {
  transport: new Set(['trips', 'orders', 'dispatch_allocations', 'workflow_engine', 'invoices']),
  stations: new Set(['stations', 'station_pos', 'purchases', 'station_kpis', 'tanks']),
  control: new Set(['fuel_consumption', 'fuel_issues', 'fuel_requests', 'sites', 'assets']),
};

export function detecterMode(pack, modules) {
  const liste = new Set(modules || []);
  const touche = (marqueurs) => [...marqueurs].some((m) => liste.has(m));
  const domaines = Object.values(MARQUEURS).filter(touche).length;
  if (domaines >= 2) return 'hybrid';
  if (pack === 'STATIONS_RESEAU' || touche(MARQUEURS.stations)) return 'stations';
  if (pack === 'CONTROLE_CARBURANT' || touche(MARQUEURS.control)) return 'control_fuel';
  return 'transport';
}

// --- créances (core/metrics.py) --------------------------------------------------------------
const FACTURES_OUVERTES = new Set(['PENDING', 'SENT', 'PARTIAL', 'PARTIALLY_PAID', 'OVERDUE']);
const statutDe = (facture) => String(facture.status ?? '').toUpperCase();
const montantOuvert = (facture) =>
  num(facture.amount_remaining !== null && facture.amount_remaining !== undefined ? facture.amount_remaining : facture.total_amount);
export const creancesOuvertes = (factures) =>
  factures.filter((f) => FACTURES_OUVERTES.has(statutDe(f))).reduce((s, f) => s + montantOuvert(f), 0);

// --- aides -----------------------------------------------------------------------------------
const valeurCommande = (commande) => {
  if (!commande) return 0;
  const livre = num(ou(commande.delivered_quantity, commande.quantity_liters));
  const prix = num(ou(commande.unit_price, commande.unit_cost));
  return num(ou(commande.total_amount, commande.amount, livre * prix));
};

const titreFile = (tripId, commande) => {
  if (commande?.reference_number) return `Commande ${commande.reference_number}`;
  if (commande?.id) return `Commande ${String(commande.id).slice(0, 8)}`;
  if (tripId) return `Mission ${String(tripId).slice(0, 8)}`;
  return 'Mission sans référence';
};

const sousTitrePreuve = (pack, trajet) => {
  const parties = [];
  if (pack?.bl_number) parties.push(`BL ${pack.bl_number}`);
  if (pack?.delivered_volume !== null && pack?.delivered_volume !== undefined) {
    parties.push(`${arrondiPair(num(pack.delivered_volume))} L`);
  }
  if (trajet?.status) parties.push(statutFr(trajet.status));
  return parties.length ? parties.join(' · ') : 'Preuve livraison validée';
};

const sousTitreBloque = (item) => {
  const statut = statutFr(item.status || 'POD_REQUIRED');
  const ecart = item.volume_variance_liters;
  if (ecart) return `${statut} · écart ${arrondiPair(Math.abs(num(ecart)))} L`;
  return `${statut} · bon de livraison signé ou revue de la finance attendu`;
};

const contratAction = (action, preconditions, effet, liens) => ({
  action,
  preconditions,
  expected_effect: effet,
  linked_effects: liens,
  idempotency: 'effet_unique_par_reference_metier',
  audit: 'audit_obligatoire',
  failure_message: 'Action bloquée si les préconditions métier ne sont pas satisfaites.',
  next_step: 'Ouvrir la mission, corriger les blocages, puis relancer l\'action.',
});

const mission = (titre, nombre, description, route, ton, action, preconditions, effet, liens) => ({
  title: titre,
  count: nombre,
  description,
  route,
  tone: ton,
  action_contract: contratAction(action, preconditions, effet, liens),
});

const STATUTS_ACTIFS = new Set([
  'CREATED', 'EN_ROUTE_TO_BORDER', 'AT_CUSTOMS_EXIT', 'CLEARED_CUSTOMS_EXIT', 'EN_ROUTE_TO_LOADING', 'LOADING',
  'EN_ROUTE_TO_ENTRY_CUSTOMS', 'AT_CUSTOMS_ENTRY', 'CLEARED_CUSTOMS_ENTRY', 'ARRIVED_AT_DEPOT',
  'EN_ROUTE_TO_DELIVERY', 'DELIVERED',
]);
const STATUTS_DOUANE = new Set(['AT_CUSTOMS_EXIT', 'AT_CUSTOMS_ENTRY']);

const somme = (liste, f) => liste.reduce((s, x) => s + f(x), 0);

export function construireSectionTransport(commandes, trajets, factures, scores = [], packs = []) {
  const actifs = trajets.filter((t) => STATUTS_ACTIFS.has(t.status) && !t.deleted);
  const douane = actifs.filter((t) => STATUTS_DOUANE.has(t.status));
  const facturees = new Set(factures.filter((f) => f.order_id).map((f) => f.order_id));
  const parCommande = new Map(commandes.filter((c) => c.id).map((c) => [c.id, c]));
  const parTrajet = new Map(trajets.filter((t) => t.id).map((t) => [t.id, t]));
  const aFacturer = commandes.filter(
    (c) => !facturees.has(c.id) && !['CANCELLED', 'PAID'].includes(String(c.status || '').toUpperCase()),
  );
  const impayes = creancesOuvertes(factures);
  const bloquants = scores.filter((s) => ['FAILED_RECONCILIATION', 'BLOCKED_AUDIT', 'POD_REQUIRED'].includes(s.status));
  const perteCarburant = somme(bloquants, (s) => num(s.estimated_loss_value));
  const litresSuspects = somme(bloquants, (s) => Math.abs(num(s.volume_variance_liters)));
  const sansPreuve = scores.filter((s) => s.status === 'POD_REQUIRED' || !s.has_delivery_proof);
  const commandesBloquees = new Set(bloquants.filter((s) => s.order_id).map((s) => s.order_id));
  const montantABloquer = somme(
    aFacturer.filter((c) => commandesBloquees.size === 0 || commandesBloquees.has(c.id)),
    (c) => num(ou(c.total_amount, c.amount)),
  );
  const pretsAFacturer = scores.filter((s) => s.billing_ready);
  const preuvesARevoir = packs.filter((p) => p.status !== null && p.status !== undefined && p.status !== 'BILLING_READY');
  const valeurPrete = somme(pretsAFacturer, (s) => valeurCommande(parCommande.get(s.order_id)));
  const valeurPreuves = somme(sansPreuve, (s) => valeurCommande(parCommande.get(s.order_id)));

  const filePrete = pretsAFacturer.slice(0, 5).map((s) => ({
    title: titreFile(s.trip_id, parCommande.get(s.order_id)),
    subtitle: sousTitrePreuve(packs.find((p) => p.trip_id === s.trip_id) || null, parTrajet.get(s.trip_id)),
    value: valeurCommande(parCommande.get(s.order_id)),
    route: s.trip_id ? `/trips/${s.trip_id}` : '/finance',
    tone: 'ok',
    action_label: 'Ouvrir la mission',
    trip_id: s.trip_id,
    order_id: s.order_id,
  }));
  const fileBloquee = [...sansPreuve, ...bloquants]
    .slice(0, 5)
    .filter((s) => s.trip_id)
    .map((s) => ({
      title: titreFile(s.trip_id, parCommande.get(s.order_id)),
      subtitle: sousTitreBloque(s),
      value: valeurCommande(parCommande.get(s.order_id)),
      route: s.trip_id ? `/mission-integrity?trip_id=${s.trip_id}` : '/mission-integrity',
      tone: s.status === 'POD_REQUIRED' ? 'warning' : 'risk',
      action_label: "Traiter l'exception",
      trip_id: s.trip_id,
      order_id: s.order_id,
      status: s.status,
    }));

  return {
    key: 'transport',
    title: 'Transport et livraisons',
    promise: 'Prioriser les pertes de carburant, les preuves manquantes et les montants facturables bloqués.',
    metrics: [
      { key: 'losses_at_risk', label: 'Pertes à risque', value: perteCarburant, format: 'money', tone: perteCarburant ? 'risk' : 'ok', at_stake: true },
      { key: 'suspect_liters', label: 'Litres suspects', value: litresSuspects, format: 'liters', tone: litresSuspects ? 'risk' : 'ok', at_stake: true },
      { key: 'billing_blocked', label: 'Montants à surveiller', value: montantABloquer, format: 'money', tone: montantABloquer ? 'risk' : 'ok', at_stake: true },
      { key: 'pod_missing', label: 'Bons de livraison manquants', value: sansPreuve.length, tone: sansPreuve.length ? 'warning' : 'ok' },
      { key: 'billing_ready_value', label: 'Prêt à facturer XOF', value: valeurPrete, format: 'money', tone: valeurPrete ? 'ok' : 'info' },
      { key: 'proof_review_value', label: 'Preuves à contrôler', value: valeurPreuves, format: 'money', tone: valeurPreuves ? 'warning' : 'ok' },
      { key: 'billing_ready_count', label: 'Prêt à facturer', value: pretsAFacturer.length, tone: pretsAFacturer.length ? 'ok' : 'info' },
      { key: 'open_receivables', label: 'Créances ouvertes', value: impayes, format: 'money', tone: impayes ? 'risk' : 'ok', at_stake: true },
      { key: 'active_trips', label: 'Trajets actifs', value: actifs.length, tone: 'info' },
    ],
    focus_queues: [
      {
        key: 'billing_ready_queue',
        title: 'À facturer maintenant',
        description: 'Missions avec bon de livraison validé, facturables immédiatement.',
        empty_state: 'Aucune mission prête à facturer.',
        items: filePrete,
      },
      {
        key: 'pod_blocked_queue',
        title: 'Preuve ou audit à contrôler',
        description: "Cash retardé faute de BL exploitable ou d'intégrité mission validée.",
        empty_state: 'Aucune anomalie de preuve détectée.',
        items: fileBloquee,
      },
    ],
    missions: [
      mission(
        'Anomalies carburant', bloquants.length, 'Missions avec écart litres/FCFA ou audit bloquant.', '/mission-integrity',
        bloquants.length ? 'risk' : 'ok', 'justify_mission_integrity',
        ['score calculé', 'écart valorisé', 'preuves sources disponibles'],
        "Suivre la facture et justifier l'écart avec trace auditée.", ['finance', 'audit', 'réconciliation volume'],
      ),
      mission(
        'Bons de livraison à traiter', sansPreuve.length + preuvesARevoir.length,
        'Livraisons non facturables faute de preuve client structurée.', '/documents',
        sansPreuve.length || preuvesARevoir.length ? 'warning' : 'ok', 'validate_proof_of_delivery',
        ['BL signé capturé', 'OCR exécuté', 'mission livrée'],
        'Contrôler la preuve en parallèle de la facturation.', ['cash-to-cash', 'preuve client', 'facturation'],
      ),
      mission(
        'Facturation avec alertes', aFacturer.length, 'Commandes sans facture prête ou nécessitant validation volume.', '/invoices',
        aFacturer.length ? 'risk' : 'ok', 'generate_invoice',
        ['livraison clôturée', 'volume réconcilié', 'stock mouvement unique'],
        'Générer une facture uniquement quand les volumes et le stock sont cohérents.', ['finance', 'audit', 'paiement client'],
      ),
      mission(
        'Douane et transit', douane.length, 'Trajets qui exigent une surveillance documentaire et GPS.', '/tracking',
        douane.length ? 'warning' : 'ok', 'control_customs_trip',
        ['document douane présent', 'dernier point GPS fiable'],
        "Passer le trajet à l'étape suivante avec preuve et localisation contrôlées.", ['workflow', 'alertes', 'carte flotte'],
      ),
    ],
  };
}

const faut = (mission_) =>
  ['risk', 'warning'].includes(mission_.tone) && Number.parseInt(mission_.count || 0, 10) > 0;

const alerteATraiter = (alerte) => String(alerte.status ?? '').trim().toLowerCase() === 'open';

export const LIBELLE_STOCK_CHARGE = 'Carburant chargé, non livré';
export const LIBELLE_STOCK_DEPOT = 'Stock au dépôt';

export function construireSectionGroupe(transport, station, control, mouvements, alertes, { hybride = true, litresCharges = null } = {}) {
  const stockCourant = calculerSoldes(mouvements).available_liters;
  const ouvertes = alertes.filter(alerteATraiter);
  const aRisque = [transport, station, control].reduce(
    (n, section) => n + (section.missions || []).filter(faut).length,
    0,
  );
  const stock =
    litresCharges !== null && litresCharges !== undefined
      ? { key: 'stock', label: LIBELLE_STOCK_CHARGE, value: Math.max(litresCharges, 0), format: 'liters', tone: 'info', basis: 'loaded', at_stake: true }
      : {
          key: 'stock',
          label: hybride ? 'Stock consolidé' : LIBELLE_STOCK_DEPOT,
          value: Math.max(stockCourant, 0),
          format: 'liters',
          tone: 'info',
          basis: 'depot',
          at_stake: true,
        };
  return {
    key: 'group',
    title: hybride ? 'Carburant du groupe' : 'Stock et alertes',
    promise: hybride
      ? 'Relier transport, stations, stock et consommation interne pour savoir où est chaque litre.'
      : 'Savoir où est chaque litre et ce qui reste à traiter.',
    metrics: [
      stock,
      {
        key: 'open_alerts',
        label: 'Alertes ouvertes',
        value: ouvertes.length,
        tone: ouvertes.length ? 'risk' : 'ok',
        hint: "Alertes automatiques qui attendent une décision, comme le compteur « à traiter » de l'écran Alertes.",
      },
      {
        key: 'risk_families',
        label: 'Missions à risque',
        value: aRisque,
        tone: aRisque ? 'warning' : 'ok',
        hint: "Nombre de familles de travaux en alerte ou à risque (anomalies carburant, preuves, facturation, douane, cuves, caisse, demandes), hors alertes.",
      },
    ],
    missions: [],
  };
}

const rang = (m) => (m.tone === 'risk' ? 0 : m.tone === 'warning' ? 1 : 2);

export function actionsPrioritaires(sections) {
  const missions = sections.flatMap((s) => (s.missions || []).filter(faut));
  return missions
    .map((m, i) => [m, i])
    .sort((a, b) => rang(a[0]) - rang(b[0]) || -Number.parseInt(a[0].count || 0, 10) + Number.parseInt(b[0].count || 0, 10) || a[1] - b[1])
    .map(([m]) => m)
    .slice(0, 5);
}

const TITRES = {
  transport: 'Commandes, trajets, volumes, stock et factures sous contrôle.',
  stations: 'Ventes, cuves, caisse, achats et marge réseau en lecture directe.',
  control_fuel: 'Demandes, sorties, engins, coûts et stock interne maîtrisés.',
  hybrid: "Une vue groupe pour suivre le litre de l'import à la vente ou consommation.",
};

const VIDE = { missions: [] };

/**
 * Cockpit complet. `stations` et `controle` sont les dernières sections connues
 * du serveur (ou absentes): leurs données ne sont pas sur l'appareil.
 */
export function construireCockpit({
  pack, modules, commandes = [], trajets = [], factures = [], mouvements = [], alertes = [],
  scores = [], packs = [], litresCharges = null, stations = null, controle = null,
}) {
  const mode = detecterMode(pack, modules);
  const transport = construireSectionTransport(commandes, trajets, factures, scores, packs);
  const station = stations || VIDE;
  const control = controle || VIDE;
  let sections;
  if (mode === 'transport') {
    sections = [transport, construireSectionGroupe(transport, VIDE, VIDE, mouvements, alertes, { hybride: false, litresCharges })];
  } else if (mode === 'stations') {
    sections = [station, construireSectionGroupe(VIDE, station, VIDE, mouvements, alertes, { hybride: false, litresCharges })];
  } else if (mode === 'control_fuel') {
    sections = [control, construireSectionGroupe(VIDE, VIDE, control, mouvements, alertes, { hybride: false, litresCharges })];
  } else {
    // Hybride: une section par métier que la licence ouvre (backend `licensed_trades`).
    const liste = new Set(modules || []);
    const avecStations = [...MARQUEURS.stations].some((m) => liste.has(m));
    const avecControle = [...MARQUEURS.control].some((m) => liste.has(m));
    const stationHybride = avecStations ? station : VIDE;
    const controleHybride = avecControle ? control : VIDE;
    sections = [
      construireSectionGroupe(transport, stationHybride, controleHybride, mouvements, alertes, { litresCharges }),
      transport,
      ...(avecStations ? [station] : []),
      ...(avecControle ? [control] : []),
    ];
  }
  return {
    mode,
    active_pack: pack ?? null,
    enabled_modules: modules || [],
    headline: TITRES[mode],
    sections,
    priority_actions: actionsPrioritaires(sections),
    acceptance_lens: [
      'Ce qui se passe maintenant',
      'Ce qui bloque les opérations',
      "Combien d'argent ou de litres sont en jeu",
      'Quelle action prendre ensuite',
    ],
  };
}
